import { useCallback } from 'react';
import { useMatch, useParams, generatePath } from 'react-router-dom';
import { useViewerId } from 'hooks/useViewerId';

import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { useReporterSubPath } from './useReporterSubPath';
import pick from 'ramda/es/pick';
import { getCaseStudyParamsSuffix } from './studyParams';
import { generateNestedReporterUrl } from 'utils/urls';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

type UrlBuilderArgs = {
  isViewer?: Nullable<boolean>;
  isReporter?: Nullable<boolean>;
  isDockedJacket?: Nullable<boolean>;
  isUndockedJacket?: Nullable<boolean>;
  isAISandbox?: Nullable<boolean>;
  isBlank?: Nullable<boolean>;
  isActive?: Nullable<boolean>;
};

type BuildUrlArgs = {
  caseSmid?: Nullable<string>;
  studySmids?: ReadonlyArray<string>;
  autoloadedStudySmids?: ReadonlyArray<string>;
  viewerId?: Nullable<string>;
};

export const useUrlBuilder = ({
  isViewer: forceViewer = null,
  isReporter: forceReporter = null,
  isDockedJacket: forceDockedJacket = null,
  isUndockedJacket: forceUndockedJacket = null,
  isAISandbox: forceAISandbox = null,
  isBlank: forceBlank = null,
  isActive: forceActive = null,
}: UrlBuilderArgs = {}): ((urlProps: BuildUrlArgs) => Nullable<string>) => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);
  const params = useParams();
  const matchViewer = useMatch(AppRoutePath.VIEWER) != null;
  const matchViewerBlank = useMatch(AppRoutePath.VIEWER_BLANK) != null;
  const matchReporter = useMatch({ path: AppRoutePath.REPORTER, end: false }) != null;
  const matchReporterBlank = useMatch({ path: AppRoutePath.REPORTER_BLANK, end: false }) != null;
  const matchDockedJacket = useMatch(AppRoutePath.PATIENT_JACKET) != null;
  const matchUndockedJacket = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED) != null;
  const matchUndockedJacketBlank = useMatch(AppRoutePath.PATIENT_JACKET_BLANK) != null;
  const matchAISandbox = useMatch(AppRoutePath.AI_SANDBOX) != null;
  const matchAISandboxBlank = useMatch(AppRoutePath.AI_SANDBOX_BLANK) != null;

  const forceUrl =
    forceViewer || forceReporter || forceDockedJacket || forceUndockedJacket || forceAISandbox;
  const isViewer = forceUrl != null ? forceViewer : matchViewer || matchViewerBlank;
  const isReporter = forceUrl != null ? forceReporter : matchReporter || matchReporterBlank;
  const isDockedJacket = forceUrl != null ? forceDockedJacket : matchDockedJacket;
  const isUndockedJacket =
    forceUrl != null ? forceUndockedJacket : matchUndockedJacket || matchUndockedJacketBlank;
  const isAISandbox = forceUrl != null ? forceAISandbox : matchAISandbox || matchAISandboxBlank;
  const isBlankPath =
    matchViewerBlank || matchUndockedJacketBlank || matchReporterBlank || matchAISandboxBlank;

  const reporterSubPath = useReporterSubPath();

  const viewerIdFromUrl = useViewerId();

  const buildUrl = useCallback(
    ({
      caseSmid,
      studySmids = [],
      autoloadedStudySmids = [],
      viewerId = viewerIdFromUrl,
    }: BuildUrlArgs) => {
      const shouldIgnoreMissingCaseSmid =
        !isClearPatientContextEnabled || (!forceBlank && !isBlankPath);

      const isMissingCaseSmid = caseSmid == null;

      if (shouldIgnoreMissingCaseSmid && isMissingCaseSmid) {
        return null;
      }

      const worklistId = caseSmid ?? (isClearPatientContextEnabled ? NOT_FOUND_CASE_ID : null);

      const shouldBuildBlank =
        !forceActive && (worklistId == null || worklistId === NOT_FOUND_CASE_ID || forceBlank);

      // add query params for any requested study smids that weren't autoloaded
      const priorStudies = studySmids
        .filter((priorSmid) => !autoloadedStudySmids.includes(priorSmid))
        .join(',');

      // add query params for autoload prior smids to skip (not included in the new list of studies)
      const skippedAutoloadedPriorStudies = autoloadedStudySmids
        .filter((autoloadedSmid) => !studySmids.includes(autoloadedSmid))
        .join(',');

      const priorsSuffix = getCaseStudyParamsSuffix(priorStudies, skippedAutoloadedPriorStudies);

      if (isViewer === true) {
        if (shouldBuildBlank) {
          return generatePath(AppRoutePath.VIEWER_BLANK, {
            windowId: viewerId ?? '0',
          });
        }

        return `${generatePath(AppRoutePath.VIEWER, {
          worklistId,
          windowId: viewerId ?? '0',
        })}${priorsSuffix}`;
      } else if (reporterSubPath != null) {
        if (shouldBuildBlank) {
          return `${generatePath(generateNestedReporterUrl(reporterSubPath, true), {
            ...pick(['templateId', 'macroId'], params),
          })}`;
        }

        return `${generatePath(generateNestedReporterUrl(reporterSubPath), {
          ...pick(['templateId', 'macroId'], params),
          worklistId,
        })}${priorsSuffix}`;
      } else if (isReporter === true) {
        if (shouldBuildBlank) {
          return `${generatePath(AppRoutePath.REPORTER_BLANK)}`;
        }

        return `${generatePath(AppRoutePath.REPORTER, { worklistId })}${priorsSuffix}`;
      } else if (isDockedJacket === true) {
        /**
         * Note that Patient Jacket only syncs if it is already on the same case,
         * (this function will not be called when the jacket is on a different case)
         * So it is safe to build prior parameters for the case
         * because we have the full study info already.
         */
        return `${generatePath(AppRoutePath.PATIENT_JACKET, { smid: worklistId })}${priorsSuffix}`;
      } else if (isUndockedJacket === true) {
        if (shouldBuildBlank) {
          return `${generatePath(AppRoutePath.PATIENT_JACKET_BLANK)}`;
        }

        return `${generatePath(AppRoutePath.PATIENT_JACKET_UNDOCKED, { smid: worklistId })}${priorsSuffix}`;
      } else if (isAISandbox === true) {
        if (shouldBuildBlank) {
          return `${generatePath(AppRoutePath.AI_SANDBOX_BLANK)}`;
        }
        return `${generatePath(AppRoutePath.AI_SANDBOX, { worklistId })}${priorsSuffix}`;
      }
    },
    [
      viewerIdFromUrl,
      isClearPatientContextEnabled,
      forceBlank,
      isBlankPath,
      forceActive,
      isViewer,
      reporterSubPath,
      isReporter,
      isDockedJacket,
      isUndockedJacket,
      isAISandbox,
      params,
    ]
  );

  return buildUrl;
};
